import { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ListChecks, HelpCircle, ArrowLeft, Flame } from "lucide-react";

export default function Tests() {
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [explains, setExplains] = useState({});
  const [busyExplain, setBusyExplain] = useState(null);

  useEffect(() => { api.get("/tests/topics").then((r) => setTopics(r.data)); }, []);

  const start = async (t) => {
    const r = await api.get(`/tests/topics/${t.id}/questions`);
    setTopic(t); setQuestions(r.data); setAnswers({}); setResult(null); setExplains({});
  };

  const submit = async () => {
    try {
      const r = await api.post("/tests/submit", { topic_id: topic.id, answers });
      setResult(r.data);
      toast.success(`Test completado: ${r.data.attempt.score}% · +${r.data.points_earned} puntos`);
      if (r.data.new_badges.length) toast.success("¡Nueva insignia desbloqueada! 🏅");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const explain = async (qid, sel) => {
    setBusyExplain(qid);
    try {
      const r = await api.post("/tests/explain", { question_id: qid, selected_index: sel });
      setExplains((x) => ({ ...x, [qid]: r.data.explanation }));
      if (r.data.cached) toast.info("Explicación reutilizada · 0 créditos");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusyExplain(null); }
  };

  if (!topic) {
    return (
      <div className="fade-up" data-testid="tests-page">
        <h1 className="text-3xl font-semibold tracking-tight mb-2">Test Dakota</h1>
        <p className="text-neutral-500 mb-8">Elige un tema y pon a prueba tus conocimientos. Si fallas, Dakota te explica el error.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {topics.map((t) => (
            <button key={t.id} data-testid={`topic-card-${t.name.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => start(t)}
                    className="text-left bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 hover:shadow-lg hover:-translate-y-1 transition-transform">
              <ListChecks className="text-orange-600 mb-3" size={22} />
              <h3 className="font-semibold text-lg">{t.name}</h3>
              <p className="text-sm text-neutral-500 mt-1">{t.description}</p>
              <p className="text-xs text-orange-700 mt-3 font-medium">{t.question_count} preguntas</p>
            </button>
          ))}
        </div>
        <div className="mt-10 bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6">
          <h2 className="font-semibold mb-1">Test de Terceros</h2>
          <p className="text-sm text-neutral-500">Los bancos de preguntas de proveedores externos estarán disponibles próximamente en esta sección.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up max-w-3xl" data-testid="test-active">
      <button data-testid="test-back-btn" onClick={() => setTopic(null)} className="flex items-center gap-1 text-sm text-neutral-500 hover:text-orange-700 mb-4">
        <ArrowLeft size={15} /> Volver a temas
      </button>
      <h1 className="text-2xl font-semibold tracking-tight mb-6">Test: {topic.name}</h1>

      {!result ? (
        <div className="space-y-6">
          {questions.map((q, qi) => (
            <div key={q.id} className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6">
              <p className="font-medium mb-4">{qi + 1}. {q.text}</p>
              <div className="space-y-2">
                {q.options.map((o, oi) => (
                  <label key={oi} className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                    answers[q.id] === oi ? "border-orange-500 bg-orange-50" : "border-neutral-200 hover:bg-neutral-50"}`}>
                    <input type="radio" name={q.id} data-testid={`q-${qi}-opt-${oi}`} checked={answers[q.id] === oi}
                           onChange={() => setAnswers({ ...answers, [q.id]: oi })} className="accent-orange-600" />
                    {o}
                  </label>
                ))}
              </div>
            </div>
          ))}
          <Button data-testid="test-submit-btn" onClick={submit} disabled={Object.keys(answers).length < questions.length}
                  className="rounded-full bg-orange-600 hover:bg-orange-700 text-white px-8">
            Corregir test
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 flex items-center justify-between" data-testid="test-result-card">
            <div>
              <p className="text-sm text-neutral-500">Resultado</p>
              <p className={`text-4xl font-semibold ${result.attempt.score >= 70 ? "text-green-600" : "text-red-600"}`}>{result.attempt.score}%</p>
              <p className="text-sm text-neutral-500 mt-1">{result.attempt.correct} de {result.attempt.total} correctas · +{result.points_earned} puntos</p>
            </div>
            <div className="flex items-center gap-2 text-amber-700 bg-amber-100 rounded-full px-4 py-2 text-sm font-semibold">
              <Flame size={15} /> Racha: {result.streak_days} días
            </div>
          </div>
          {result.results.map((r, i) => (
            <div key={r.question_id} className={`bg-white border rounded-xl p-6 ${r.ok ? "border-green-200" : "border-red-200"}`}>
              <p className="font-medium">{i + 1}. {r.text}</p>
              <p className={`text-sm mt-2 ${r.ok ? "text-green-700" : "text-red-700"}`}>
                {r.ok ? "✓ Correcta" : `✗ Tu respuesta: ${r.selected != null ? r.options[r.selected] : "—"}`}
              </p>
              {!r.ok && <p className="text-sm text-neutral-600 mt-1">Correcta: <strong>{r.options[r.correct_index]}</strong></p>}
              {!r.ok && !explains[r.question_id] && (
                <Button data-testid={`explain-btn-${i}`} onClick={() => explain(r.question_id, r.selected ?? 0)} variant="outline" size="sm"
                        disabled={busyExplain === r.question_id}
                        className="mt-3 rounded-full border-orange-300 text-orange-700 hover:bg-orange-50">
                  <HelpCircle size={14} className="mr-1.5" />
                  {busyExplain === r.question_id ? "Dakota está pensando…" : "Explícame el error"}
                </Button>
              )}
              {explains[r.question_id] && (
                <div className="mt-4 bg-orange-50 border border-orange-100 rounded-xl p-4 text-sm whitespace-pre-wrap leading-relaxed" data-testid={`explanation-${i}`}>
                  <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider mb-2">Explicación de Dakota</p>
                  {explains[r.question_id]}
                </div>
              )}
            </div>
          ))}
          <Button onClick={() => setTopic(null)} variant="outline" className="rounded-full" data-testid="test-finish-btn">Volver a temas</Button>
        </div>
      )}
    </div>
  );
}
